import { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Menu, Bell, LogOut, User, Moon, Sun, Monitor } from 'lucide-react';
import { useAuthStore } from '@/store/auth.store';
import { resolveDisplayName } from '@/lib/user-display';
import { useThemeStore, type ThemeMode } from '@/store/theme.store';
import { useApprovals } from '@/hooks/useApprovals';
import { useOutsideClick } from '@/hooks/useOutsideClick';

interface HeaderProps {
  onMenuClick: () => void;
}

export function Header({ onMenuClick }: HeaderProps) {
  const { t } = useTranslation();
  const { user, logout } = useAuthStore();
  const { mode, resolvedTheme, setMode } = useThemeStore();
  const { pendingCount } = useApprovals();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showThemeMenu, setShowThemeMenu] = useState(false);

  // UP-17 (2026-05-17): outside-click closes popup. Memoize callback чтобы
  // hook не пересоздавал listener каждый render.
  const closeUserMenu = useCallback(() => setShowUserMenu(false), []);
  const closeThemeMenu = useCallback(() => setShowThemeMenu(false), []);
  const userMenuRef = useOutsideClick<HTMLDivElement>(showUserMenu, closeUserMenu);
  const themeMenuRef = useOutsideClick<HTMLDivElement>(showThemeMenu, closeThemeMenu);

  const handleLogout = async () => {
    await logout();
    setShowUserMenu(false);
  };

  const handleThemeSelect = (newMode: ThemeMode) => {
    setMode(newMode);
    setShowThemeMenu(false);
  };

  const ThemeIcon = resolvedTheme === 'dark' ? Sun : Moon;

  return (
    <header className="sticky top-0 z-40 glass border-b border-gray-200 dark:border-gray-700">
      <div className="flex h-16 items-center justify-between px-4 md:px-6">
        {/* Левая часть - бургер меню и лого */}
        <div className="flex items-center gap-4">
          <button
            onClick={onMenuClick}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 md:hidden"
            aria-label={t('header.openMenu')}
          >
            <Menu className="h-5 w-5" />
          </button>

          <Link to="/dashboard" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-primary flex items-center justify-center">
              <span className="text-white font-bold text-sm">{t('common.brand.mark')}</span>
            </div>
            <span className="font-semibold text-lg hidden sm:block">
              {t('common.brand.name')}
            </span>
          </Link>
        </div>

        {/* Правая часть - уведомления и профиль */}
        <div className="flex items-center gap-2">
          {/* S4-35 (human_decision (б)): переключателя языка у залогиненного
               нет — язык меняется только осью профиля в «Настройки → Язык и
               регион» (useProfileLanguageSync + LocaleSection). Аноним
               переключает язык на публичных страницах (LanguageSwitcher). */}
          {/* Кнопка темы с выпадающим меню */}
          <div className="relative" ref={themeMenuRef}>
            <button
              onClick={() => setShowThemeMenu(!showThemeMenu)}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
              aria-label={t('header.toggleTheme')}
            >
              <ThemeIcon className="h-5 w-5" />
            </button>

            {/* Выпадающее меню темы */}
            {showThemeMenu && (
              <div className="absolute right-0 mt-2 w-40 rounded-lg bg-white dark:bg-gray-800 shadow-lg border border-gray-200 dark:border-gray-700 animate-slide-in">
                <div className="p-1">
                  <button
                    onClick={() => handleThemeSelect('light')}
                    className={`w-full flex items-center gap-2 px-3 py-2 text-left text-sm rounded-lg ${
                      mode === 'light'
                        ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400'
                        : 'hover:bg-gray-100 dark:hover:bg-gray-700'
                    }`}
                  >
                    <Sun className="h-4 w-4" />
                    {t('header.themeLight')}
                  </button>
                  <button
                    onClick={() => handleThemeSelect('dark')}
                    className={`w-full flex items-center gap-2 px-3 py-2 text-left text-sm rounded-lg ${
                      mode === 'dark'
                        ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400'
                        : 'hover:bg-gray-100 dark:hover:bg-gray-700'
                    }`}
                  >
                    <Moon className="h-4 w-4" />
                    {t('header.themeDark')}
                  </button>
                  <button
                    onClick={() => handleThemeSelect('system')}
                    className={`w-full flex items-center gap-2 px-3 py-2 text-left text-sm rounded-lg ${
                      mode === 'system'
                        ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400'
                        : 'hover:bg-gray-100 dark:hover:bg-gray-700'
                    }`}
                  >
                    <Monitor className="h-4 w-4" />
                    {t('header.themeSystem')}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Уведомления */}
          <Link
            to="/approvals"
            className="relative p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
            aria-label={t('header.notifications')}
          >
            <Bell className="h-5 w-5" />
            {pendingCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-xs text-white font-medium">
                {pendingCount > 9 ? '9+' : pendingCount}
              </span>
            )}
          </Link>

          {/* Меню пользователя */}
          <div className="relative" ref={userMenuRef}>
            <button
              onClick={() => setShowUserMenu(!showUserMenu)}
              className="flex items-center gap-2 p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
            >
              {user?.photoUrl ? (
                <img
                  src={user.photoUrl}
                  alt={resolveDisplayName(user, t('header.user'))}
                  className="h-8 w-8 rounded-full object-cover"
                />
              ) : (
                <div className="h-8 w-8 rounded-full bg-primary-100 dark:bg-primary-900 flex items-center justify-center">
                  <User className="h-4 w-4 text-primary-600 dark:text-primary-400" />
                </div>
              )}
              <span className="text-sm font-medium hidden md:block">
                {resolveDisplayName(user, t('header.user'))}
              </span>
            </button>

            {/* Выпадающее меню */}
            {showUserMenu && (
              <div className="absolute right-0 mt-2 w-56 rounded-lg bg-white dark:bg-gray-800 shadow-lg border border-gray-200 dark:border-gray-700 animate-slide-in">
                <div className="p-3 border-b border-gray-200 dark:border-gray-700">
                  {/* UP-25 (2026-05-25): unified через resolveDisplayName helper.
                       UP-18 (2026-05-17) ввёл fallback chain firstName+lastName →
                       username → «Пользователь», но Dashboard + Header top-right
                       были не консистентны. Сейчас все три места используют
                       один источник истины — lib/user-display.ts. */}
                  <p className="font-medium">
                    {resolveDisplayName(user, t('header.user'))}
                  </p>
                  {user?.username && (user?.firstName || user?.lastName) && (
                    <p className="text-sm text-gray-500">@{user.username}</p>
                  )}
                </div>
                <div className="p-1">
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg"
                  >
                    <LogOut className="h-4 w-4" />
                    {t('auth.logout')}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}
